/**
 * CP-9.3 — Phase 9 Admin Disputes surface (Workforce Partners).
 *
 * Exports:
 *  - AdminDisputesInbox      /app/admin/disputes
 *  - AdminDisputeDetail      /app/admin/disputes/:id
 *  - AdminReachability       /app/admin/reachability  (§23 tile)
 *
 * Admin-only routes. Provides:
 *  - Dispute lifecycle controls (accept/reject/assign/resolve/reopen)
 *  - Remediation actions (apply + rollback)
 *  - Financial holds (place + release)
 *  - Deanonymization grants (two-admin rule honored client-side; server
 *    enforces the lock)
 *  - Evidence ledger with redaction
 *  - Notification Reachability tile (§23) — read-only diagnostic
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, AlertTriangle, ShieldAlert, Activity, Mail,
          Eye, EyeOff, Loader2, RefreshCw, Inbox, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";

const STATE_COLOR = {
  proposed: "bg-amber-100 text-amber-800",
  under_review: "bg-blue-100 text-blue-800",
  rejected: "bg-rose-100 text-rose-800",
  denied: "bg-rose-100 text-rose-800",
  upheld_full: "bg-emerald-100 text-emerald-800",
  upheld_partial: "bg-emerald-100 text-emerald-800",
  withdrawn: "bg-slate-100 text-slate-800",
  stale: "bg-slate-200 text-slate-800",
};

function StateBadge({ s }) {
  return <span data-testid={`admin-state-${s}`}
    className={`text-xs px-2 py-0.5 rounded ${STATE_COLOR[s] || "bg-slate-100 text-slate-800"}`}>{s}</span>;
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-CA",
    { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ============================================================================
// Inbox
// ============================================================================

export function AdminDisputesInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterState, setFilterState] = useState("");
  const [sweeping, setSweeping] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const q = filterState ? `?state=${filterState}` : "";
      const r = await api(`/admin/disputes${q}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterState]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const r = await api("/admin/disputes/sla-sweep", { method: "POST" });
      toast.success(`Swept: ${r.warned} warned · ${r.auto_closed} auto-closed`);
      load();
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally {
      setSweeping(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6" data-testid="admin-disputes-inbox-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Disputes (admin)</h1>
            <p className="text-sm text-slate-500 mt-1">
              Triage, accept, remediate and close platform disputes.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/app/admin/reachability"
                  data-testid="link-reachability"
                  className="text-sm px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50">
              <BarChart3 className="inline h-4 w-4 mr-1" />
              Reachability tile
            </Link>
            <button onClick={runSweep} disabled={sweeping}
              data-testid="btn-sla-sweep"
              className="text-sm px-3 py-1.5 bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50">
              {sweeping ? <Loader2 className="inline h-4 w-4 animate-spin" /> : <RefreshCw className="inline h-4 w-4 mr-1" />}
              Run SLA sweep
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          {["", "proposed", "under_review", "rejected", "denied",
              "upheld_full", "upheld_partial", "stale"].map(s => (
            <button key={s || "all"}
              data-testid={`filter-${s || "all"}`}
              onClick={() => setFilterState(s)}
              className={`text-xs px-3 py-1 rounded border ${
                filterState === s
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-300"}`}>
              {s || "all"}
            </button>
          ))}
        </div>

        {loading && <div className="text-slate-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-center py-16 text-slate-400" data-testid="admin-empty">
            <Inbox className="mx-auto h-10 w-10 mb-2" /> No disputes match.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <table className="w-full bg-white rounded border border-slate-200">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
              <tr>
                <th className="px-4 py-2">#</th>
                <th className="px-4 py-2">Subject</th>
                <th className="px-4 py-2">State</th>
                <th className="px-4 py-2">Reason</th>
                <th className="px-4 py-2">SLA due</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y">
              {rows.map(d => (
                <tr key={d.id}
                  data-testid={`admin-row-${d.id}`}
                  onClick={() => navigate(`/app/admin/disputes/${d.id}`)}
                  className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">#{d.id}</td>
                  <td className="px-4 py-2 text-slate-600">{d.subject_kind}</td>
                  <td className="px-4 py-2"><StateBadge s={d.state} /></td>
                  <td className="px-4 py-2 text-slate-500">{d.reason_code}</td>
                  <td className="px-4 py-2 text-slate-500">{fmt(d.sla_due_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}

// ============================================================================
// Detail
// ============================================================================

export function AdminDisputeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKind, setActionKind] = useState("admin_note_only");
  const [actionAmount, setActionAmount] = useState("");
  const [actionInvoice, setActionInvoice] = useState("");
  const [actionPayout, setActionPayout] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [resolveSummary, setResolveSummary] = useState("");
  const [resolveState, setResolveState] = useState("upheld_partial");

  const load = async () => {
    try {
      const dr = await api(`/admin/disputes/${id}`);
      setD(dr);
      const ev = await api(`/admin/disputes/${id}/evidence?include_redacted=true`);
      setEvidence(ev.data || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doAccept = async () => {
    try {
      await api(`/admin/disputes/${id}/accept`, { method: "POST", body: {} });
      toast.success("Accepted");
      load();
    } catch (e) { toast.error(e.problem?.title || e.message); }
  };
  const doReject = async () => {
    const note = prompt("Reject reason?");
    if (!note) return;
    try {
      await api(`/admin/disputes/${id}/reject`, { method: "POST", body: { note } });
      toast.success("Rejected"); load();
    } catch (e) { toast.error(e.problem?.title || e.message); }
  };
  const doResolve = async () => {
    if (!resolveSummary.trim()) return toast.error("Summary required");
    try {
      await api(`/admin/disputes/${id}/resolve`,
        { method: "POST", body: { to_state: resolveState, resolution_summary: resolveSummary } });
      toast.success(`Resolved: ${resolveState}`);
      setResolveSummary("");
      load();
    } catch (e) { toast.error(e.problem?.title || e.message); }
  };
  const doRemediation = async () => {
    const payload = { action_kind: actionKind, notes: actionNotes };
    if (actionAmount) payload.amount = parseFloat(actionAmount);
    if (actionInvoice) payload.target_invoice_id = parseInt(actionInvoice);
    if (actionPayout) payload.target_payout_id = parseInt(actionPayout);
    if (actionAmount) payload.currency = "USD";
    try {
      await api(`/admin/disputes/${id}/remediation-actions`,
        { method: "POST", body: payload });
      toast.success("Remediation applied");
      setActionAmount(""); setActionInvoice(""); setActionPayout(""); setActionNotes("");
      load();
    } catch (e) { toast.error(e.problem?.title || e.message); }
  };
  const doRollback = async (actId) => {
    const reason = prompt("Rollback reason?");
    if (!reason) return;
    try {
      await api(`/admin/disputes/${id}/remediation-actions/${actId}/rollback`,
        { method: "POST", body: { reason } });
      toast.success("Rolled back"); load();
    } catch (e) { toast.error(e.problem?.title || e.message); }
  };
  const doRedact = async (eid) => {
    const reason = prompt("Redaction reason (required, immutable)?");
    if (!reason) return;
    try {
      await api(`/admin/disputes/${id}/evidence/${eid}/redact`,
        { method: "POST", body: { reason } });
      toast.success("Evidence redacted"); load();
    } catch (e) { toast.error(e.problem?.title || e.message); }
  };
  const doReleaseHold = async (holdId) => {
    const note = prompt("Release note (required)?");
    if (!note) return;
    try {
      await api(`/admin/disputes/${id}/holds/${holdId}/release`,
        { method: "POST", body: { release_note: note } });
      toast.success("Hold released"); load();
    } catch (e) { toast.error(e.problem?.title || e.message); }
  };

  if (loading) return <Layout><div className="p-6 text-slate-500">Loading…</div></Layout>;
  if (!d) return null;
  const open = d.state === "proposed" || d.state === "under_review";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6" data-testid="admin-dispute-detail-page">
        <button onClick={() => navigate(-1)}
          data-testid="btn-back"
          className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-4">
          <ChevronLeft className="h-4 w-4" /> Back to inbox
        </button>

        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900">Dispute #{d.id}</h1>
              <StateBadge s={d.state} />
            </div>
            <div className="flex gap-2">
              {d.state === "proposed" && (
                <>
                  <button onClick={doAccept} data-testid="btn-admin-accept"
                    className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700">Accept</button>
                  <button onClick={doReject} data-testid="btn-admin-reject"
                    className="text-sm px-3 py-1.5 border border-rose-300 text-rose-700 rounded hover:bg-rose-50">Reject</button>
                </>
              )}
            </div>
          </div>
          <div className="text-sm text-slate-500">{d.subject_kind} · {d.reason_code}
            {d.filer_user_id && <> · filer user {d.filer_user_id}</>}
          </div>
          {d.filer_narrative && (
            <div className="mt-3 p-3 bg-slate-50 border-l-4 border-slate-300 rounded text-sm whitespace-pre-wrap">
              {d.filer_narrative}
            </div>
          )}
        </div>

        {/* Active financial holds — operational state, separate from immutable records */}
        {d.financial_holds?.length > 0 && (
          <Section title="Financial holds" testId="admin-holds">
            <table className="w-full text-sm">
              <tbody className="divide-y">
                {d.financial_holds.map(h => (
                  <tr key={h.id} data-testid={`admin-hold-${h.id}`}>
                    <td className="py-2 font-medium">#{h.id}</td>
                    <td className="py-2">{h.hold_kind}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        h.state === "active" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{h.state}</span>
                    </td>
                    <td className="py-2 text-slate-500">{fmt(h.placed_at)}</td>
                    <td className="py-2 text-right">
                      {h.state === "active" && (
                        <button onClick={() => doReleaseHold(h.id)}
                          data-testid={`btn-release-hold-${h.id}`}
                          className="text-xs text-blue-600 hover:underline">Release</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* Remediation log */}
        <Section title="Remediation history" testId="admin-remediation">
          {d.remediation_actions?.length === 0 && <div className="text-sm text-slate-400">No actions yet.</div>}
          <ul className="divide-y">
            {d.remediation_actions?.map(a => (
              <li key={a.id} data-testid={`admin-remediation-${a.id}`}
                className="py-2 text-sm flex items-center justify-between">
                <div>
                  <span className="font-medium">{a.action_kind}</span>
                  {a.amount && <span className="text-slate-500"> · {a.amount} {a.currency}</span>}
                  {a.external_ref && <span className="text-xs text-slate-400 ml-2">{a.external_ref}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    a.state === "applied" ? "bg-blue-50 text-blue-700" :
                    a.state === "rolled_back" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-700"}`}>
                    {a.state}
                  </span>
                  {a.state === "applied" && (
                    <button onClick={() => doRollback(a.id)}
                      data-testid={`btn-rollback-${a.id}`}
                      className="text-xs text-rose-600 hover:underline">Rollback</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {open && (
            <div className="mt-4 border-t pt-3 grid grid-cols-2 gap-2">
              <select value={actionKind} onChange={e => setActionKind(e.target.value)}
                data-testid="select-admin-action-kind"
                className="text-sm border-slate-300 rounded">
                <option value="admin_note_only">Admin note only</option>
                <option value="credit_memo_issue">Credit memo</option>
                <option value="refund_client">Refund client</option>
                <option value="hold_payout">Hold payout</option>
                <option value="release_payout">Release payout</option>
                <option value="rating_strike">Rating strike</option>
                <option value="timesheet_correction">Timesheet correction</option>
                <option value="waive_cancel_fee">Waive cancel fee</option>
              </select>
              <input data-testid="input-admin-action-amount"
                type="number" step="0.01" placeholder="Amount (optional)"
                value={actionAmount} onChange={e => setActionAmount(e.target.value)}
                className="text-sm border-slate-300 rounded" />
              <input data-testid="input-admin-action-invoice"
                type="number" placeholder="Target invoice id"
                value={actionInvoice} onChange={e => setActionInvoice(e.target.value)}
                className="text-sm border-slate-300 rounded" />
              <input data-testid="input-admin-action-payout"
                type="number" placeholder="Target payout id"
                value={actionPayout} onChange={e => setActionPayout(e.target.value)}
                className="text-sm border-slate-300 rounded" />
              <textarea data-testid="input-admin-action-notes"
                placeholder="Notes (recommended for credit memos)"
                value={actionNotes} onChange={e => setActionNotes(e.target.value)}
                className="text-sm border-slate-300 rounded col-span-2" rows={2} />
              <button onClick={doRemediation} data-testid="btn-admin-apply"
                className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 col-span-2">
                Apply remediation
              </button>
            </div>
          )}
        </Section>

        {/* Resolution */}
        {d.state === "under_review" && (
          <Section title="Resolve dispute" testId="admin-resolve">
            <select value={resolveState} onChange={e => setResolveState(e.target.value)}
              data-testid="select-admin-resolve-state"
              className="text-sm border-slate-300 rounded mr-2">
              <option value="upheld_full">Upheld (full)</option>
              <option value="upheld_partial">Upheld (partial)</option>
              <option value="denied">Denied</option>
            </select>
            <textarea data-testid="input-admin-resolve-summary"
              placeholder="Resolution summary (visible to filer + counterparty)"
              value={resolveSummary} onChange={e => setResolveSummary(e.target.value)}
              className="text-sm border-slate-300 rounded w-full mt-2" rows={3} />
            <button onClick={doResolve} data-testid="btn-admin-resolve"
              className="mt-2 text-sm px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700">
              Resolve
            </button>
          </Section>
        )}

        {/* Evidence ledger with redaction */}
        <Section title="Evidence ledger" testId="admin-evidence">
          {evidence.length === 0 && <div className="text-sm text-slate-400">No evidence.</div>}
          <ul className="divide-y">
            {evidence.map(ev => (
              <li key={ev.id} data-testid={`admin-evidence-${ev.id}`} className="py-2 text-sm">
                <div className="flex justify-between">
                  <div>
                    <span className="font-medium">{ev.title}</span>
                    <span className="text-xs text-slate-500 ml-2">{ev.evidence_type} · by {ev.uploaded_by_role} (user #{ev.uploaded_by_user_id})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {ev.is_redacted ? (
                      <span data-testid={`admin-evidence-redacted-${ev.id}`}
                        className="text-xs px-2 py-0.5 bg-rose-100 text-rose-800 rounded">REDACTED</span>
                    ) : (
                      <button onClick={() => doRedact(ev.id)}
                        data-testid={`btn-admin-redact-${ev.id}`}
                        className="text-xs text-rose-600 hover:underline">Redact</button>
                    )}
                  </div>
                </div>
                {ev.description && <div className="text-slate-600 mt-1">{ev.description}</div>}
                <div className="text-xs text-slate-400 mt-1">{fmt(ev.created_at)} · audit_event_id: {ev.audit_event_id}</div>
              </li>
            ))}
          </ul>
        </Section>

        {/* Status timeline */}
        {d.transitions?.length > 0 && (
          <Section title="Status timeline" testId="admin-timeline">
            <ol className="relative border-l border-slate-200 pl-4 space-y-2">
              {d.transitions.map((t, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[21px] top-1.5 h-3 w-3 rounded-full bg-blue-500" />
                  <div className="text-sm">{t.from_state || "initial"} → {t.to_state} <span className="text-xs text-slate-500 ml-1">{fmt(t.occurred_at)} · {t.actor_kind}</span></div>
                  {t.reason_note && <div className="text-xs text-slate-500">{t.reason_note}</div>}
                </li>
              ))}
            </ol>
          </Section>
        )}
      </div>
    </Layout>
  );
}

// ============================================================================
// Reachability Tile (§23)
// ============================================================================

export function AdminReachability() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const load = async () => {
    try {
      const r = await api("/admin/notifications/reachability");
      setData(r);
      const h = await api("/admin/notifications/health-strip");
      setHealth(h);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const triggerRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await api("/admin/notifications/reachability/refresh",
        { method: "POST", body: {} });
      toast.success(`Refreshed ${r.refreshed_users} users`);
      load();
    } catch (e) { toast.error(e.problem?.title || e.message); }
    finally { setRefreshing(false); }
  };
  const sendVerification = async (uid) => {
    setResendingId(uid);
    try {
      const r = await api(
        `/admin/notifications/reachability/${uid}/send-verification`,
        { method: "POST", body: {} });
      toast.success(`Verification resent · ${r.rolling_count} in window`);
      load();
    } catch (e) {
      const code = e.problem?.code;
      if (code === "VERIFICATION_RESEND_COOLDOWN") {
        toast.error("Cooldown active — try again later");
      } else {
        toast.error(e.problem?.title || e.message);
      }
    } finally { setResendingId(null); }
  };

  if (loading) return <Layout><div className="p-6 text-slate-500">Loading…</div></Layout>;
  if (!data) return null;
  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6" data-testid="admin-reachability-page">
        <Link to="/app/admin/disputes"
              className="text-sm text-slate-500 hover:text-slate-700 mb-4 inline-flex items-center gap-1">
          <ChevronLeft className="h-4 w-4" /> Disputes
        </Link>
        <div className="flex items-center justify-between mt-2 mb-1">
          <h1 className="text-2xl font-semibold text-slate-900">Notification reachability</h1>
          <button onClick={triggerRefresh} disabled={refreshing}
            data-testid="btn-refresh-snapshots"
            className="text-sm px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw className={`inline h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh now
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Diagnostic tile · window {data.window_days} days · bounce threshold {data.bounce_threshold}
        </p>

        {/* CP-10.2 Notification Health Strip (Lock §4) */}
        {health && (
          <div data-testid="health-strip"
            className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
            <div data-testid="health-listen">
              <div className="text-slate-500 uppercase tracking-wide">LISTEN</div>
              <div className={`font-semibold ${health.listen_enabled ? "text-emerald-700" : "text-rose-700"}`}>
                {health.listen_enabled ? "ENABLED" : "DISABLED"}
              </div>
            </div>
            <div data-testid="health-notify-per-min">
              <div className="text-slate-500 uppercase tracking-wide">Notify/min</div>
              <div className="font-semibold">{health.last_bucket.notify_per_min}</div>
            </div>
            <div data-testid="health-fallback">
              <div className="text-slate-500 uppercase tracking-wide">Fallback polls/min</div>
              <div className="font-semibold">{health.last_bucket.fallback_polls_per_min}</div>
            </div>
            <div data-testid="health-events">
              <div className="text-slate-500 uppercase tracking-wide">Dispatched</div>
              <div className="font-semibold">{health.last_bucket.events_dispatched}</div>
            </div>
            <div data-testid="health-snapshot-freshness">
              <div className="text-slate-500 uppercase tracking-wide">Snapshot rows · newest</div>
              <div className="font-semibold text-slate-700">
                {health.snapshot_freshness.snapshot_row_count} · {fmt(health.snapshot_freshness.newest_computed_at)}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Stat testId="stat-active-users" label="Active users in window"
                value={data.total_active_users} />
          <Stat testId="stat-unreachable-count" label="Unreachable"
                value={data.unreachable_count}
                accent={data.unreachable_count > 0 ? "rose" : "emerald"} />
          <Stat testId="stat-avg-score" label="Average reachability"
                value={`${data.average_reachability_score}%`}
                accent={data.average_reachability_score >= 85 ? "emerald" :
                          data.average_reachability_score >= 60 ? "amber" : "rose"} />
        </div>

        <div className="bg-white rounded-lg border border-slate-200" data-testid="reachability-table">
          <div className="px-4 py-3 border-b text-sm font-semibold text-slate-700">Active recipients</div>
          {data.users.length === 0 && <div className="p-6 text-sm text-slate-400">No activity in window.</div>}
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
              <tr>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Class.</th>
                <th className="px-4 py-2">Dispatched</th>
                <th className="px-4 py-2">Delivered</th>
                <th className="px-4 py-2">Bounces</th>
                <th className="px-4 py-2">Score</th>
                <th className="px-4 py-2">Last resend</th>
                <th className="px-4 py-2">30d</th>
                <th className="px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.users.map(u => (
                <tr key={u.user_id} data-testid={`reachability-row-${u.user_id}`}>
                  <td className="px-4 py-2 text-slate-700">{u.email || `user #${u.user_id}`}</td>
                  <td className="px-4 py-2"
                      data-testid={`reach-classification-${u.user_id}`}>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      u.classification === "verified" ? "bg-emerald-100 text-emerald-800" :
                      u.classification === "pending_verification" ? "bg-amber-100 text-amber-800" :
                      u.classification === "bounced" || u.classification === "complained" || u.classification === "unreachable" ? "bg-rose-100 text-rose-800" :
                      u.classification === "suppressed" ? "bg-slate-200 text-slate-800" :
                      "bg-slate-100 text-slate-700"}`}>
                      {u.classification}
                    </span>
                  </td>
                  <td className="px-4 py-2">{u.dispatched_count}</td>
                  <td className="px-4 py-2">{u.delivered_count}</td>
                  <td className="px-4 py-2">{u.bounce_count}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      u.reachability_score >= 85 ? "bg-emerald-100 text-emerald-800" :
                      u.reachability_score >= 60 ? "bg-amber-100 text-amber-800" :
                      "bg-rose-100 text-rose-800"}`}>
                      {u.reachability_score}%
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500"
                      data-testid={`reach-last-resend-${u.user_id}`}>
                    {u.resend?.last_resend_at ? fmt(u.resend.last_resend_at) : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs"
                      data-testid={`reach-rolling-${u.user_id}`}>
                    {u.resend?.rolling_count ?? 0}
                  </td>
                  <td className="px-4 py-2">
                    {!u.email_verified && (
                      <button onClick={() => sendVerification(u.user_id)}
                        data-testid={`btn-resend-verification-${u.user_id}`}
                        disabled={resendingId === u.user_id
                                    || (u.resend?.cooldown_until
                                          && new Date(u.resend.cooldown_until) > new Date())}
                        className="text-xs px-2 py-1 border border-blue-300 text-blue-700 rounded hover:bg-blue-50 disabled:opacity-40">
                        <Mail className="inline h-3 w-3 mr-1" />
                        {(u.resend?.cooldown_until && new Date(u.resend.cooldown_until) > new Date())
                          ? "Cooldown"
                          : "Send verification"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function Section({ title, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">{title}</h2>
      {children}
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50",
    amber: "border-amber-200 bg-amber-50",
    rose: "border-rose-200 bg-rose-50",
  };
  return (
    <div data-testid={testId}
      className={`rounded-lg border p-4 ${accent ? colors[accent] : "border-slate-200 bg-white"}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

export default AdminDisputesInbox;
